import type { GlSheetRowVO } from '@/services/accountService/glInvoiceService';
import type { DrCrItem, InvoiceEntryRow } from '@/pages/components/account/gl-invoice/create-stepper/context';
import { buildColumnDefIndex, rowFromGlSheet } from '@/pages/components/account/gl-invoice/create-stepper/context';
import type { GlColumnDef } from '@/services/accountService/glInvoiceService';
import { stripDupSuffix } from '@/pages/components/account/gl-invoice/mode/tabs/bankColumnUtils';
import { signedAmountForSave } from '@/pages/components/account/gl-invoice/mode/glEditEntryHelpers';

/** 把一行的借贷金额和 Ex-rate 写入 columnValues。CR 存负数，DR 存正数；汇率按绝对值存到 *-RATE。 */
export function writeDrCrColumnValues(items: DrCrItem[], columnValues: Record<string, number>) {
    for (const item of items) {
        if (item.columnKey && item.amount !== '') {
            const amt = signedAmountForSave(item.side, item.amount);
            if (amt != null && amt !== 0) {
                columnValues[item.columnKey] = amt;
            }
        }
        if (!item.columnKey || !item.exRate) continue;
        const rate = Math.abs(parseFloat(item.exRate));
        if (isNaN(rate) || rate === 0) continue;
        const sep = item.columnKey.indexOf('|');
        if (sep < 0) continue;
        const fs = item.columnKey.slice(0, sep);
        const gl = stripDupSuffix(item.columnKey.slice(sep + 1));
        columnValues[`${fs}|${gl}-RATE`] = rate;
    }
}

/** GL 行 → 编辑行 */
export function glRowsToEditRows(rows: GlSheetRowVO[], columnDefs: GlColumnDef[]): InvoiceEntryRow[] {
    const index = columnDefs.length > 0 ? buildColumnDefIndex(columnDefs) : undefined;
    return rows.map((r) => rowFromGlSheet(r, index));
}

/** 从编辑行汇总当期合计（对应 Excel Row 6） */
export function computeSummaryCurrentPeriod(rows: InvoiceEntryRow[]): Record<string, number> {
    const summary: Record<string, number> = {};
    for (const r of rows) {
        for (const item of r.drCrItems) {
            if (item.columnKey && item.amount) {
                const amt = signedAmountForSave(item.side, item.amount);
                if (amt != null && amt !== 0) {
                    summary[item.columnKey] = (summary[item.columnKey] ?? 0) + amt;
                }
            }
            if (item.columnKey && item.exRate) {
                const rate = Math.abs(parseFloat(item.exRate));
                if (!isNaN(rate) && rate !== 0) {
                    const sep = item.columnKey.indexOf('|');
                    if (sep >= 0) {
                        const rateKey = `${item.columnKey.slice(0, sep)}|${stripDupSuffix(item.columnKey.slice(sep + 1))}-RATE`;
                        summary[rateKey] = (summary[rateKey] ?? 0) + rate;
                    }
                }
            }
        }
    }
    return summary;
}

/** 将编辑行转为 save-gl-preview 请求体 */
export function editRowsToGlSheetRows(rows: InvoiceEntryRow[]): GlSheetRowVO[] {
    return rows
        .filter((r) => r.date || r.description || r.voucherNo)
        .map((r) => {
            const columnValues: Record<string, number> = {};
            writeDrCrColumnValues(r.drCrItems, columnValues);
            const amounts = r.drCrItems
                .filter((item: DrCrItem) => item.columnKey !== null && item.amount !== '')
                .map((item: DrCrItem) => parseFloat(item.amount))
                .filter((amt: number) => !isNaN(amt));
            return {
                invoiceId: r.invoiceId ?? '',
                date: r.date || null,
                description: r.description || null,
                voucherNo: r.voucherNo || null,
                invoiceNo: r.invoiceNo || null,
                invoiceDate: r.invoiceDate || null,
                chequeNo: r.chequeNo || null,
                details: r.details || null,
                price: amounts.length > 0 ? Math.abs(Math.max(...amounts)) : null,
                columnValues,
                validationWarnings: r.validationWarnings,
            };
        });
}

/** 计算被删除的 invoiceId（编辑前存在、保存 payload 中不再出现） */
export function findDeletedInvoiceIds(
    initialIds: Iterable<string>,
    saveRows: GlSheetRowVO[],
): string[] {
    const current = new Set(
        saveRows.map((r) => r.invoiceId).filter((id): id is string => !!id),
    );
    return [...initialIds].filter((id) => id && !current.has(id));
}
